import { generateId } from '../../../../utils'
import { cloneDeep, difference } from 'lodash'

import { createEditor, Editor, Node, Text, Transforms } from 'slate'

// Remove unsupported leafs/marks from the fragment
// Filter table, lift and reference like complex elements if not supported
// Remove/unwrap unsupported inline elements ( keeping the text context )
// Completely remove unsupported void elements as they do not have text content

const slateFragmentDeserializer = (htmlContent, editor) => {
  try {
    const slateFragment = decodeSlateFragmentData(htmlContent)
    const isCustomToolbarMode = editor.toolbarMode === 'custom'

    if (!isCustomToolbarMode ) {
      updateUids(slateFragment)
      return slateFragment?.[0]
    }

    const customToolbarComponents = editor.customToolbarComponents
    const supportedLeafNodes = customToolbarComponents?.['v2.leaf'] ?? []
    const supportedElementNodes = customToolbarComponents?.['v2.element'] ?? []

    const blockElements = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code']

    const voidElements = ['hr', 'embed', 'img', 'social-embeds']

    const inlineElements = ['a']

    const mockEditor = createEditor()
    mockEditor.children = slateFragment

    const marksToRemove = difference(getAllMarksInEditor(mockEditor), supportedLeafNodes)
    const blockElementsToRemove = difference(blockElements, supportedElementNodes)
    const inlineElementsToRemove = difference(inlineElements, supportedElementNodes)
    const voidElementsToRemove = difference(voidElements, supportedElementNodes)
    // TODO: handle plugin elements

    Transforms.select(mockEditor, [0])

    // Filter simple elements
    filterMarks(mockEditor, marksToRemove)

    filterListElements(mockEditor, supportedElementNodes)

    filterReferenceElements(mockEditor, supportedElementNodes)

    filterTableElements(mockEditor, supportedElementNodes)

    filterOtherBlockElements(mockEditor, blockElementsToRemove)

    filterVoidElements(mockEditor, voidElementsToRemove)

    filterInlineElements(mockEditor, inlineElementsToRemove)

    const clonedFragment = cloneDeep(mockEditor.children[0])
    updateUids(clonedFragment)
    return clonedFragment
  } catch (e) {
    console.error(e)
    return null
  }
}
// Text
function getAllMarksInEditor(editor): string[] {
  const marksSet = new Set<string>()

  for (const [node] of Editor.nodes(editor, { at: [], match: Text.isText, mode: 'lowest' })) {
    if (node) {
      Object.keys(node).forEach((key) => {
        if (key !== 'text') {
          marksSet.add(key)
        }
      })
    }
  }

  return Array.from(marksSet)
}
function filterMarks(editor, marksToRemove: string[]) {
  for (const mark of marksToRemove) {
    Editor.removeMark(editor, mark)
  }
}
// Blocks
function filterOtherBlockElements(editor, blockElementsToRemove: string[]) {
  Transforms.setNodes(editor, { type: 'p', attrs: {} } as Partial<Node>, {
    at: [],
    match: (n) => blockElementsToRemove.includes(n['type']),
    mode: 'all'
  })
}
// Voids
function filterVoidElements(editor, voidElements: string[]) {
  Transforms.removeNodes(editor, {
    at: [],
    match: (n) => voidElements.includes(n['type']),
    mode: 'lowest'
  })
}
// Inlines
function filterInlineElements(editor, inlineElementsToRemove: string[]) {
  // unwrap nodes
  Transforms.unwrapNodes(editor, {
    at: [],
    match: (n) => inlineElementsToRemove.includes(n['type']),
    mode: 'lowest'
  })
}
// Lists
function filterListElements(editor, supportedElements: string[]) {
  // Iterate over all nodes with a `type`
  if (supportedElements?.includes('li')) return

  Transforms.unwrapNodes(editor, {
    match: (n: any) => {
      return ['ol', 'ul'].includes(n.type)
    },
    at: [],
    split: true,
    mode: 'all'
  })

  Transforms.setNodes(
    editor,
    {
      type: 'p',
      attrs: {}
    } as Partial<Node>,
    {
      match: (n: any) => {
        return n.type === 'li'
      }
    }
  )
}
//Table
function filterTableElements(editor, supportedElements: string[]) {
  if (supportedElements?.includes('table')) return
  Transforms.unwrapNodes(editor, {
    at: [],
    match: (n: any) => {
      return ['table', 'tr', 'tbody', 'trgrp', 'thead', 'td', 'th'].includes(n.type)
    },
    mode: 'all'
  })
}
// References
function filterReferenceElements(editor, elements: string[]) {
  if (!elements.includes('reference')) {
    Transforms.unwrapNodes(editor, {
      at: [],
      match: (n: any) => {
        return n.type === 'reference' && n?.attrs?.['display-type'] === 'link' && n?.attrs?.['type'] === 'entry'
      },
      mode: 'all'
    })

    Transforms.removeNodes(editor, {
      at: [],
      match: (n: any) => {
        return n.type === 'reference' && n?.attrs?.['display-type'] !== 'link' && n?.attrs?.['type'] === 'entry'
      },
      mode: 'lowest'
    })
  }

  if (!elements.includes('assetReference')) {
    Transforms.unwrapNodes(editor, {
      at: [],
      match: (n: any) => {
        return n.type === 'reference' && n?.attrs?.['display-type'] === 'link' && n?.attrs?.['type'] === 'asset'
      },
      mode: 'all'
    })

    Transforms.removeNodes(editor, {
      at: [],
      match: (n: any) => {
        return n.type === 'reference' && n?.attrs?.['display-type'] !== 'link' && n?.attrs?.['type'] === 'asset'
      },
      mode: 'lowest'
    })
  }
}

export function updateUids(json) {
  const children = Array.isArray(json) ? json : [json]

  children.forEach((child) => {
    if (child.hasOwnProperty('type')) {
      child.uid = generateId()
    }
    if (child.children) {
      updateUids(child.children)
    }
  })
}

export const decodeSlateFragmentData = (parsedHtml) => {
  const fragmentElement = parsedHtml.querySelector('[data-slate-fragment]')
  if (!fragmentElement) {
    return null
  }
  const fragmentData = fragmentElement.getAttribute('data-slate-fragment')
  if (!fragmentData) {
    return null
  }
  return JSON.parse(
    decodeURIComponent(atob(fragmentData))
  )
}

export default slateFragmentDeserializer
